import Pusher from 'pusher-js';

import store from 'state/store';
import {
  apiActivityPing,
  setAssignedLeadIcon,
  setAssignedLeadId,
  setMayHaveMissedBroadcastEvents,
  setSystemPresenceEngagement,
} from 'state/concierge.state';
import { apiFetchLead, updateLeadCommunications, updateLeadEntity } from 'state/lead.state';
import { setEndCall, setLiveTransferSuccess } from 'state/call.state';
import {
  setConnectionState,
  setIsAuthed,
  setNotification,
  setUpdateAvailable,
} from 'state/app.state';
import { browserHistory, sendWebNotification } from 'utils/functions';
import { LogoutWarningMilliseconds } from 'utils/constants';
import { AppCopyText, CallTypes, ConciergeActions, ConnectionState } from 'typings';

Pusher.logToConsole = true;
Pusher.log = (msg) => {
  console.log(msg);
};

interface PusherMessage {
  data: any;
}

interface PusherChannels {
  [channel: string]: string;
}

class PusherService {
  private pusher: any;

  private channels: PusherChannels = {};

  constructor() {
    this.pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY || '', {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER,
    });

    this.pusher.connection.bind('connected', () => {
      Pusher.log('Connected');
      store.dispatch(setConnectionState(ConnectionState.CONNECTED));
    });

    this.pusher.connection.bind('connecting', () => {
      Pusher.log('Connecting');
      store.dispatch(setMayHaveMissedBroadcastEvents(true));
    });

    this.pusher.connection.bind('unavailable', () => {
      Pusher.log('Unavailable');
      store.dispatch(setConnectionState(ConnectionState.UNAVAILABLE));
      store.dispatch(setMayHaveMissedBroadcastEvents(true));
    });

    this.pusher.connection.bind('error', (err: any) => {
      if (err.error.data.code === 4004) {
        Pusher.log(`Error: ${err}`);
        store.dispatch(
          setNotification({
            type: 'error',
            text: AppCopyText.PUSHER_CONNECTION_ERROR,
          })
        );
      }
    });

    this.channels.currentAlertsChannel = '';
    this.channels.currentLeadChannel = '';
    this.channels.currentLDEChannel = '';
    this.channels.currentConciergeChannel = '';
    this.channels.currentCustomerChannel = '';
  }

  subscribeToAlerts() {
    if (this.channels.currentAlertsChannel) return;

    const name = 'alerts';
    const channel = this.pusher.subscribe(name);
    this.channels.currentAlertsChannel = name;

    let updateAvailableTimeout: any = null;

    channel.bind('App\\Events\\SystemUpdate\\ConciergeConsole', (message: PusherMessage) => {
      // clear any pending timeout before actioning this event
      if (updateAvailableTimeout) {
        clearTimeout(updateAvailableTimeout);
      }

      if (message?.data?.isAvailable) {
        // apply a jitter so not all concierges are notified at exactly the same time; add a random delay up to 10 minutes
        const delay: number = Math.round(Math.random() * 1000 * 600);
        updateAvailableTimeout = setTimeout(() => store.dispatch(setUpdateAvailable(true)), delay);

        console.log('System update available. Notification scheduled after delay:', delay);
      } else {
        store.dispatch(setUpdateAvailable(false));
      }
    });
  }

  subscribeToLDE(conciergeId: number) {
    if (this.channels.currentLDEChannel) {
      this.pusher.unsubscribe(this.channels.currentLDEChannel);
    }

    if (!conciergeId) return;

    const ldeChannel = `lde-cid-${conciergeId}`;
    const channel = this.pusher.subscribe(ldeChannel);
    this.channels.currentLDEChannel = ldeChannel;

    // listen for and handle leads distributed to me
    channel.bind('App\\Events\\Lead\\LeadUpdateRep', (message: PusherMessage) => {
      const leadId = message?.data?.leadId;
      const leadIcon = message?.data?.icon;

      if (leadId) {
        const currentPathname = window.location.pathname;
        const newPathname = `/${leadId}`;

        store.dispatch({ type: 'RESET_LEAD' });
        store.dispatch(setAssignedLeadId(leadId));
        store.dispatch(setAssignedLeadIcon(leadIcon));
        sendWebNotification('New Lead', 'You have a new distributed lead in the Verse Console.');

        if (currentPathname === newPathname) {
          // we are currently viewing the lead that the LDE distributed, so refetch the lead
          store.dispatch(apiFetchLead(leadId));
        } else {
          // update browser url
          browserHistory.push(newPathname);
        }
      }
    });
  }

  unsubscribeFromLeadChannel() {
    if (this.channels.currentLeadChannel) {
      this.pusher.unsubscribe(this.channels.currentLeadChannel);
      this.channels.currentLeadChannel = '';
    }
  }

  subscribeToLeadChannel(leadId: string) {
    if (this.channels.currentLeadChannel) {
      this.pusher.unsubscribe(this.channels.currentLeadChannel);
    }

    if (!leadId) return;

    const leadChannel = `lead-${leadId}`;
    const channel = this.pusher.subscribe(leadChannel);
    this.channels.currentLeadChannel = leadChannel;

    // subscribe to lead updates
    channel.bind('App\\Events\\Lead\\LeadUpdate', (message: PusherMessage) => {
      if (message.data?.id !== leadId) return;

      const leadData = message.data.lead || {};
      store.dispatch(updateLeadEntity(leadData));
    });

    // status/action updates
    channel.bind('App\\Events\\Lead\\LeadUpdateStatus', (message: PusherMessage) => {
      if (message.data?.leadId !== leadId) return;

      const leadData = message.data.lead || {};
      store.dispatch(updateLeadEntity(leadData));
    });

    // Notes
    channel.bind('App\\Events\\Note\\NoteCreate', (message: PusherMessage) => {
      if (message.data?.leadId !== leadId) return;

      const note = { ...message.data.note, commType: ConciergeActions.NOTE };
      store.dispatch(updateLeadCommunications(note));
    });

    // Inbound/Outbound sms
    channel.bind('App\\Events\\Message\\MessageCreate', (message: PusherMessage) => {
      if (message.data?.leadId !== leadId) return;

      const sms = { ...message.data.message, commType: ConciergeActions.SMS };
      store.dispatch(updateLeadCommunications(sms));
    });

    // Inbound/Outbound Emails
    channel.bind('App\\Events\\Email\\EmailCreate', (message: PusherMessage) => {
      if (message.data?.leadId !== leadId) return;

      const email = { ...message.data.email, commType: ConciergeActions.EMAIL };
      store.dispatch(updateLeadCommunications(email));
    });

    // Inbound/Outbound calls
    // inbound call complete
    channel.bind('App\\Events\\Call\\Completed', (message: PusherMessage) => {
      const answered = message?.data?.call?.answered;

      if (!answered) {
        store.dispatch(setEndCall());
        store.dispatch(
          setNotification({
            type: 'info',
            text: AppCopyText.INBOUND_CALL_ENDED,
          })
        );
      }
    });

    // the automated system presence
    channel.bind('SystemPresence', (message: PusherMessage) => {
      store.dispatch(setSystemPresenceEngagement(!!message.data.isEngaged));
    });
  }

  subscribeToConcierge(conciergeId: number) {
    if (this.channels.currentConciergeChannel) {
      this.pusher.unsubscribe(this.channels.currentConciergeChannel);
    }

    if (!conciergeId) return;

    const conciergeChannel = `concierge-${conciergeId}`;
    const channel = this.pusher.subscribe(conciergeChannel);
    this.channels.currentConciergeChannel = conciergeChannel;

    // listen for concierge logout broadcast event
    channel.bind('App\\Events\\Concierge\\Logout', (message: PusherMessage) => {
      if (message.data?.id !== conciergeId) return;

      store.dispatch(setIsAuthed(false));
    });

    // send notification that user is active
    channel.bind('App\\Events\\Concierge\\Ping', (message: PusherMessage) => {
      if (message.data?.id !== conciergeId) return;
      const sessionExpiringSoon =
        message.data?.timeRemaining && message.data.timeRemaining <= LogoutWarningMilliseconds;

      store.dispatch(apiActivityPing(sessionExpiringSoon));
    });

    // outbound call failed
    channel.bind('App\\Events\\Call\\Failed', (message: PusherMessage) => {
      const callErrorMessage = message?.data?.call?.errorMessage;

      if (callErrorMessage) {
        store.dispatch(
          setNotification({
            type: 'info',
            text: callErrorMessage,
          })
        );
      }
    });

    // concierge finished an outbound call
    channel.bind('App\\Events\\Call\\Completed', (message: PusherMessage) => {
      const callType = message?.data?.call?.callType;

      if (callType === CallTypes.ISA_OUTBOUND) {
        const call = {
          ...(message?.data?.call ?? {}),
          ...(message?.data?.amendments ?? {}),
          commType: ConciergeActions.CALL,
          callType: CallTypes.OUTBOUND,
        };
        store.dispatch(updateLeadCommunications(call));
      }
    });
  }

  unsubscribeFromCustomerChannel() {
    if (this.channels.currentCustomerChannel) {
      this.pusher.unsubscribe(this.channels.currentCustomerChannel);
      this.channels.currentCustomerChannel = '';
    }
  }

  subscribeToCustomerChannel(customerId: number) {
    if (this.channels.currentCustomerChannel) {
      this.pusher.unsubscribe(this.channels.currentCustomerChannel);
    }

    if (!customerId) return;

    const currentChannel = `customer-${customerId}`;
    const channel = this.pusher.subscribe(currentChannel);
    this.channels.currentCustomerChannel = currentChannel;

    // subscribe to updates relating to this customer
    channel.bind('App\\Events\\Conference\\CustomerJoin', (message: PusherMessage) => {
      if (customerId !== Number(message.data?.id)) return;

      store.dispatch(setLiveTransferSuccess(true));
    });
  }

  unsubscribeFromAllChannels() {
    for (const [channelName, currentChannel] of Object.entries(this.channels)) {
      if (currentChannel) {
        this.pusher.unsubscribe(currentChannel);
        this.channels[channelName] = '';
      }
    }
  }
}

const pusher = new PusherService();
export default pusher;
